import { NAME_FILTERS } from 'constants/dataSets';
import { DEFAULT_BOARD_INSTANCE_GROUP_TYPE } from 'constants/general';
import { getFilterIdentifier, isCountryCodeFilter } from 'utils/valueChainHelpers';

import type IFilterData from 'pages/CompanyAdminPages/CompanyMap/components/ValueChain/interfaces/IFilterData';
import type IOrgStructureItem from 'store/slices/dataSets/interfaces/IOrgStructureItem';
import type { TBoardInstanceGroupType } from 'store/slices/valueChainMap/interfaces/IBoardInstanceGroup';
import type { IUserFiltersPayload } from 'store/slices/valueChainMap/interfaces/IUserFilters';

// The board opens with connected items visible. A saved false is the only off state.
export const DEFAULT_FOLLOW_CONNECTIONS = true;

export type TListPayloadKey =
  | 'suppliers' | 'customers' | 'products' | 'assets'
  | 'connections' | 'countryCodes' | 'profitCentres';

export type TCataloguePayloadKey = 'suppliers' | 'customers' | 'products' | 'assets' | 'connections';

export const LIST_PAYLOAD_KEYS: TListPayloadKey[] = [
  'suppliers', 'customers', 'products', 'assets',
  'connections', 'countryCodes', 'profitCentres',
];

// Payload list key -> the IFilterData.type it is rebuilt as. Country codes and
// profit centres are resolved differently and are deliberately not in this map.
export const FILTER_TYPE_BY_PAYLOAD_KEY: Record<TCataloguePayloadKey, string> = {
  suppliers: 'supplier',
  customers: 'customer',
  products: 'product',
  assets: 'balance-sheet-asset',
  connections: NAME_FILTERS.CONNECTION,
};

const PAYLOAD_KEY_BY_FILTER_TYPE: Record<string, TListPayloadKey> = {
  supplier: 'suppliers',
  customer: 'customers',
  product: 'products',
  'balance-sheet-asset': 'assets',
  [NAME_FILTERS.CONNECTION]: 'connections',
};

export interface IToSavedPayloadParams {
  selectedFilters: IFilterData[],
  followConnections: boolean,
  groupByType: TBoardInstanceGroupType,
}

export const toSavedPayload = ({
  selectedFilters,
  followConnections,
  groupByType,
}: IToSavedPayloadParams): IUserFiltersPayload => {
  const buckets: Record<TListPayloadKey, string[]> = {
    suppliers: [],
    customers: [],
    products: [],
    assets: [],
    connections: [],
    countryCodes: [],
    profitCentres: [],
  };

  let revenue: IUserFiltersPayload['revenue'];

  selectedFilters.forEach((item) => {
    if (item.type === NAME_FILTERS.REVENUE) {
      if (item.min !== undefined && item.max !== undefined) {
        revenue = { min: Number(item.min), max: Number(item.max) };
      }
      return;
    }

    if (item.type === NAME_FILTERS.ORG_STRUCTURE) {
      buckets.profitCentres.push(getFilterIdentifier(item));
      return;
    }

    if (isCountryCodeFilter(item.type)) {
      buckets.countryCodes.push(getFilterIdentifier(item));
      return;
    }

    const payloadKey = PAYLOAD_KEY_BY_FILTER_TYPE[item.type];
    // An unrecognised type is dropped: emitting it would reject the whole request.
    if (payloadKey) {
      buckets[payloadKey].push(getFilterIdentifier(item));
    }
  });

  return {
    followConnections,
    groupByType,
    ...buckets,
    ...(revenue ? { revenue } : {}),
  };
};

// Canonical form used only to compare two payloads. Lists are sorted, so a
// different chip order does not read as a different selection, and absent keys
// collapse onto the defaults the backend would apply.
export const serializeSavedPayload = (payload: IUserFiltersPayload): string => JSON.stringify([
  payload.followConnections ?? DEFAULT_FOLLOW_CONNECTIONS,
  payload.groupByType ?? DEFAULT_BOARD_INSTANCE_GROUP_TYPE,
  LIST_PAYLOAD_KEYS.map((key) => [...(payload[key] || [])].sort()),
  payload.revenue ? [payload.revenue.min, payload.revenue.max] : null,
]);

export interface IFromSavedPayloadParams {
  payload: IUserFiltersPayload,
  catalogue: IFilterData[],
  orgStructureLeaves: IOrgStructureItem[],
}

export interface IFromSavedPayloadResult {
  filters: IFilterData[],
  followConnections: boolean,
  groupByType: TBoardInstanceGroupType,
}

export const fromSavedPayload = ({
  payload,
  catalogue,
  orgStructureLeaves,
}: IFromSavedPayloadParams): IFromSavedPayloadResult => {
  const filters: IFilterData[] = [];

  (Object.keys(FILTER_TYPE_BY_PAYLOAD_KEY) as TCataloguePayloadKey[]).forEach((payloadKey) => {
    const filterType = FILTER_TYPE_BY_PAYLOAD_KEY[payloadKey];
    (payload[payloadKey] || []).forEach((identifier) => {
      const match = catalogue.find((entry) => entry.type === filterType
        && getFilterIdentifier(entry) === identifier);
      // A stale identifier is dropped; the surviving filters still apply.
      if (match) {
        filters.push(match);
      }
    });
  });

  (payload.countryCodes || []).forEach((identifier) => {
    const match = catalogue.find((entry) => isCountryCodeFilter(entry.type)
      && getFilterIdentifier(entry) === identifier);
    if (match) {
      filters.push(match);
    }
  });

  (payload.profitCentres || []).forEach((identifier) => {
    const leaf = orgStructureLeaves.find((item) => getFilterIdentifier(item) === identifier);
    if (leaf) {
      filters.push({
        name: leaf.name,
        uniqueIdentifier: leaf.uniqueIdentifier,
        min: undefined,
        max: undefined,
        type: NAME_FILTERS.ORG_STRUCTURE,
        category: NAME_FILTERS.ORG_STRUCTURE,
      });
    }
  });

  if (payload.revenue) {
    filters.push({
      name: NAME_FILTERS.REVENUE,
      uniqueIdentifier: undefined,
      min: String(payload.revenue.min),
      max: String(payload.revenue.max),
      type: NAME_FILTERS.REVENUE,
      category: NAME_FILTERS.REVENUE,
    });
  }

  return {
    filters,
    followConnections: payload.followConnections ?? DEFAULT_FOLLOW_CONNECTIONS,
    groupByType: payload.groupByType ?? DEFAULT_BOARD_INSTANCE_GROUP_TYPE,
  };
};
